import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiX, FiEdit2 } from 'react-icons/fi';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { hasBillingRole } from '../utils/roles';

// StorageAgreementPanel -- shown above the fee grid on the admin
// Customer Detail > Fees tab. Lets ops toggle a customer between
// per-pallet storage (default) and flat-rate storage, and edit the
// current agreement without leaving the page. Agreement history is
// preserved (ending an agreement doesn't delete it, just sets an
// end_date) so the panel can show a full audit trail.
//
// API endpoints (admin-gated):
//   GET    /api/admin/customers/:id/storage-agreements
//   POST   /api/admin/customers/:id/storage-agreements
//   PUT    /api/admin/customers/:id/storage-agreements/:agreementId
//   DELETE /api/admin/customers/:id/storage-agreements/:agreementId

const formatMoney = (value) => {
  if (value == null || value === '') return '--';
  const n = Number(value);
  if (Number.isNaN(n)) return '--';
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(n);
};

const formatDate = (value) => {
  if (!value) return '--';
  try {
    return new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return value;
  }
};

const StorageAgreementPanel = ({ customerId }) => {
  // Agreement mutations are role-gated on the backend (RF15).
  const { user } = useAuth();
  const canMutateBilling = hasBillingRole(user);
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [saving, setSaving] = useState(false);
  // Default start_date to today so the user sees a populated date
  // and doesn't accidentally post-date the flat rate (a future
  // start_date means the agreement isn't active yet, which breaks
  // the customer-facing snapshot).
  const todayStr = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ sqft: '', monthly_amount: '', start_date: todayStr, notes: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/customers/${customerId}/storage-agreements`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (res.ok) {
        setActive(data.active || null);
        setHistory(Array.isArray(data.history) ? data.history : []);
      } else {
        showError(data.message || 'Failed to load storage agreements');
      }
    } catch (err) {
      showError('Failed to load storage agreements');
    } finally {
      setLoading(false);
    }
  }, [customerId, showError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async () => {
    if (!form.sqft || !form.monthly_amount) {
      showError('Square footage and monthly amount are required');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`/api/admin/customers/${customerId}/storage-agreements`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          sqft: Number(form.sqft),
          monthly_amount: Number(form.monthly_amount),
          start_date: form.start_date || undefined,
          notes: form.notes || undefined
        })
      });
      const data = await res.json();
      if (res.ok) {
        showSuccess('Storage agreement created');
        setShowForm(false);
        setForm({ sqft: '', monthly_amount: '', start_date: todayStr, notes: '' });
        fetchData();
      } else {
        showError(data.message || 'Failed to create storage agreement');
      }
    } catch (err) {
      showError('Failed to create storage agreement');
    } finally {
      setSaving(false);
    }
  };

  const handleEnd = async () => {
    if (!active) return;
    if (!window.confirm('End this agreement? The customer will return to per-pallet storage billing starting today.')) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/admin/customers/${customerId}/storage-agreements/${active.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (res.ok) {
        showSuccess('Agreement ended');
        fetchData();
      } else {
        showError(data.message || 'Failed to end agreement');
      }
    } catch (err) {
      showError('Failed to end agreement');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white shadow-md rounded-lg p-6 mb-6 text-sm text-gray-500">
        Loading storage agreement...
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg p-6 mb-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-medium text-gray-900">Flat-Rate Storage</h3>
          <p className="text-xs text-gray-500 mt-1">
            Override for customers renting fixed sqft instead of per-pallet. When active, the monthly
            storage cron invoices the flat rate instead of calculating per-pallet charges.
          </p>
        </div>
        {!active && !showForm && canMutateBilling && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center whitespace-nowrap px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 flex-shrink-0"
          >
            <FiPlus className="mr-1 h-3.5 w-3.5" /> Switch to Flat Rate
          </button>
        )}
      </div>

      {active && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="text-xs uppercase font-semibold text-amber-900 tracking-wide">Active Flat Rate</div>
              <div className="mt-2 text-2xl font-bold text-amber-900">
                {formatMoney(active.monthly_amount)}
                <span className="text-sm font-normal text-amber-700"> / month</span>
              </div>
              <div className="mt-1 text-sm text-amber-900">
                {active.sqft.toLocaleString()} sqft rented
              </div>
              {(() => {
                const startsInFuture = active.start_date && active.start_date > todayStr;
                return (
                  <div className="mt-2 text-xs text-amber-700">
                    {startsInFuture ? 'Starts' : 'Active since'} {formatDate(active.start_date)}
                    {active.CreatedBy?.name ? ` · created by ${active.CreatedBy.name}` : ''}
                  </div>
                );
              })()}
              {active.start_date > todayStr && (
                <div className="mt-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800">
                  <strong>Not yet in effect.</strong> This agreement's start date is in the future,
                  so per-pallet billing still applies until {formatDate(active.start_date)}. The
                  customer-facing storage page will not show the flat rate until then.
                </div>
              )}
              {active.notes && (
                <div className="mt-2 text-xs text-amber-800 italic">{active.notes}</div>
              )}
            </div>
            {canMutateBilling && (
            <button
              type="button"
              onClick={handleEnd}
              disabled={saving}
              className="text-xs font-medium text-red-700 hover:text-red-900 disabled:opacity-50 inline-flex items-center whitespace-nowrap flex-shrink-0"
              title="Return to per-pallet storage billing"
            >
              <FiX className="mr-1 h-3.5 w-3.5" /> Switch Back to Per-Pallet
            </button>
            )}
          </div>
          <div className="mt-3 pt-3 border-t border-amber-200 text-xs text-amber-800">
            While flat rate is active, per-pallet storage fees are hidden on this customer's fee schedule and the
            monthly cron invoices the flat rate directly. Per-pallet rates reactivate automatically when switched back.
          </div>
        </div>
      )}

      {!active && !showForm && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
          This customer is billed per pallet using the storage rates below. Use <strong>Switch to Flat Rate</strong> above to
          switch them to a fixed monthly amount based on rented sqft.
        </div>
      )}

      {showForm && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
          <div className="text-sm font-semibold text-blue-900">Switch to Flat Rate Storage</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-blue-900 mb-1">Square Feet</label>
              <input
                type="number"
                min="1"
                value={form.sqft}
                onChange={(e) => setForm((f) => ({ ...f, sqft: e.target.value }))}
                placeholder="1000"
                className="w-full border border-blue-200 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-blue-900 mb-1">Monthly Rate ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.monthly_amount}
                onChange={(e) => setForm((f) => ({ ...f, monthly_amount: e.target.value }))}
                placeholder="2500.00"
                className="w-full border border-blue-200 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-blue-900 mb-1">Start Date</label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                className="w-full border border-blue-200 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-blue-700 mt-1">Defaults to today</p>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-blue-900 mb-1">Notes</label>
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Optional -- e.g. contract reference, negotiated terms"
              className="w-full border border-blue-200 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCreate}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Activating...' : 'Activate Flat Rate'}
            </button>
            <button
              type="button"
              onClick={() => { setShowForm(false); setForm({ sqft: '', monthly_amount: '', start_date: todayStr, notes: '' }); }}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowHistory((s) => !s)}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
          >
            {showHistory ? 'Hide' : 'Show'} {history.length} past flat-rate period{history.length === 1 ? '' : 's'}
          </button>
          {showHistory && (
            <div className="mt-3 space-y-2">
              {history.map((h) => (
                <div key={h.id} className="text-xs text-gray-600 border border-gray-200 rounded p-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium text-gray-900">
                        {h.sqft.toLocaleString()} sqft @ {formatMoney(h.monthly_amount)}/month
                      </div>
                      <div>{formatDate(h.start_date)} → {formatDate(h.end_date)}</div>
                    </div>
                  </div>
                  {h.notes && <div className="mt-1 italic text-gray-500">{h.notes}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StorageAgreementPanel;
